import React, { useState, useEffect } from "react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import GameModal from "./GameModal"

function SortableGame({ game, onDelete, onEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: game.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-2"
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-move p-2 hover:bg-gray-200 rounded"
          title="Drag to reorder"
        >
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </button>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">
              #{game.order_in_show}
            </span>
            <h4 className="font-semibold text-gray-800">{game.game_type}</h4>
            {game.needs_askfor && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                Needs Ask For
              </span>
            )}
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
              {game.votes_count || 0} {game.votes_count === 1 ? 'vote' : 'votes'}
            </span>
          </div>
          {game.what_happened && (
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">What Happened:</span> {game.what_happened}
            </p>
          )}
          {game.scene_recap && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Scene Recap:</span> {game.scene_recap}
            </p>
          )}
          {game.live_askfor && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Live Askfor:</span> {game.live_askfor}
            </p>
          )}
          {game.presenter && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Presenter:</span> {game.presenter}
            </p>
          )}
          {game.ask_for && (
            <div className="mt-2 p-2 bg-yellow-50 rounded text-sm">
              <span className="font-medium">Ask For:</span> {game.ask_for.prompt}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onEdit(game)}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-semibold"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(game.id)}
            className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-semibold"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

export default function ShowGamesManager({ show, onUpdate }) {
  const [games, setGames] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingGame, setEditingGame] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (show) {
      fetchGames()
    }
  }, [show?.id])

  const fetchGames = async () => {
    try {
      const response = await fetch(`/api/admin/games?show_id=${show.id}`)
      const data = await response.json()
      setGames(data.sort((a, b) => a.order_in_show - b.order_in_show))
    } catch (error) {
      console.error("Error fetching games:", error)
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = games.findIndex((g) => g.id === active.id)
      const newIndex = games.findIndex((g) => g.id === over.id)

      const newGames = arrayMove(games, oldIndex, newIndex)

      // Update order_in_show for all games
      const updatedGames = newGames.map((game, index) => ({
        ...game,
        order_in_show: index + 1,
      }))

      setGames(updatedGames)

      // Save new order to backend
      try {
        await Promise.all(
          updatedGames.map((game) =>
            fetch(`/api/admin/games/${game.id}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
              },
              body: JSON.stringify({
                game: { order_in_show: game.order_in_show },
              }),
            })
          )
        )
        if (onUpdate) onUpdate()
      } catch (error) {
        console.error("Error updating game order:", error)
        fetchGames() // Revert on error
      }
    }
  }

  const handleSave = () => {
    fetchGames()
    setModalOpen(false)
    setEditingGame(null)
    if (onUpdate) onUpdate()
  }

  const handleEdit = (game) => {
    setEditingGame(game)
    setModalOpen(true)
  }

  const handleAddNew = () => {
    console.log("handleAddNew called");
    setEditingGame(null)
    setModalOpen(true)
    console.log("modalOpen should now be true");
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingGame(null)
  }

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this game?")) return

    try {
      await fetch(`/api/admin/games/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
      })
      fetchGames()
      if (onUpdate) onUpdate()
    } catch (error) {
      console.error("Error deleting game:", error)
      alert("Error deleting game")
    }
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border-t-2 border-purple-200">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-lg font-semibold text-gray-700">
          Games ({games.length})
        </h4>
        <button
          onClick={handleAddNew}
          className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded font-semibold text-sm"
        >
          + Add Game
        </button>
      </div>

      {console.log("Rendering, modalOpen:", modalOpen)}
      {modalOpen && (
        <GameModal
          show={show}
          game={editingGame}
          gamesCount={games.length}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}

      {games.length === 0 ? (
        <p className="text-gray-500 text-center py-4 text-sm">
          No games yet. Add your first game above.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={games} strategy={verticalListSortingStrategy}>
            {games.map((game) => (
              <SortableGame
                key={game.id}
                game={game}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}
